/**
 * Customer video review Shorts, keyed by tour/destination.
 * YouTube video IDs extracted from youtube.com/shorts/... URLs.
 */

function reviews(destination, ids, quote) {
  return ids.map((id, i) => ({
    id,
    name: `Traveller Review ${i + 1}`,
    destination,
    rating: 5,
    quote: quote || `Real trip moments from our ${destination} guests.`,
  }));
}

export const VIDEO_REVIEWS = {
  'spiti-kinnaur': reviews('Spiti Kinnaur', [
    'YlQ-fP7tCa8',
    '4sgUJyehtzw',
    '69qjcea4a38',
    'MgR1OWouJ4o',
    'mefNxRfbJog',
  ]),
  manali: reviews('Manali', [
    'AcXrLooBJiQ',
    'TYYapL14gog',
    'Nq1JYf-AANM',
    '9ibuItszGlA',
    'q2g-OJ_P_Vc',
  ]),
  shimla: reviews('Shimla', [
    'VYLlJmpzB9g',
    'iZzHiHyFIWI',
  ]),
  himachal: reviews('Himachal', [
    'FXF9lhEkEV8',
    'b80Mvq_ESJU',
    'ukzxm7CJ68s',
    'X2AnLNvA1m8',
  ]),
  kashmir: reviews('Kashmir', [
    'qpOPr2fY8TY',
    'da5Gz_T8Uvk',
  ]),
  'leh-ladakh': reviews('Leh Ladakh', [
    'vwl_dPIeQL4',
    '7_zViNpDkC0',
    'H-sRh9qvFzU',
    'REF4iExPRSQ',
    '8tdDWRnGDXs',
  ]),
  'shimla-manali': reviews('Shimla Manali', [
    '-HDBd0vo_W8',
    'bEncZdm5bhU',
  ]),
  dharamshala: reviews('Dharamshala', [
    '249FqzYMOws',
    '3EgJpa8_qUU',
    '_RHm9LZ5jdw',
  ]),
  'shimla-manali-taxi': reviews('Shimla Manali Taxi', [
    'pdLX7XucNeE',
    'Y1NIlbW6kbQ',
  ]),
};

/** Default mixed reviews for pages without a destination-specific set */
export const DEFAULT_VIDEO_REVIEWS = [
  ...VIDEO_REVIEWS.himachal.slice(0, 1),
  ...VIDEO_REVIEWS.kashmir.slice(0, 1),
  ...VIDEO_REVIEWS['leh-ladakh'].slice(0, 1),
  ...VIDEO_REVIEWS['spiti-kinnaur'].slice(0, 1),
];

/**
 * Map page slugs / route keys → video review set key.
 */
const SLUG_TO_KEY = {
  // Himachal tour detail pages
  'kinnaur-spiti-tour': 'spiti-kinnaur',
  'kinnaur-tour': 'spiti-kinnaur',
  'manali-tour': 'manali',
  'shimla-tour': 'shimla',
  'himachal-tour': 'himachal',
  'shimla-manali-tour': 'shimla-manali',
  'dharamshala-dalhousie-tour': 'dharamshala',

  // Sub-regions (/explore/:slug)
  'spiti-valley': 'spiti-kinnaur',
  kinnaur: 'spiti-kinnaur',
  shimla: 'shimla',
  manali: 'manali',
  leh: 'leh-ladakh',

  // Domestic destinations
  'spiti-kinnaur': 'spiti-kinnaur',
  'himachal-pradesh': 'himachal',
  'jammu-kashmir': 'kashmir',
  'leh-ladakh': 'leh-ladakh',

  // Destinations pages
  kashmir: 'kashmir',

  // Taxi / holiday packages
  'himachal-taxi-tours': 'shimla-manali-taxi',
  'shimla-manali-dharamshala-dalhousie-amritsar-taxi': 'shimla-manali-taxi',
  'himalayan-delight-shimla-manali': 'shimla-manali',
  'snowy-shimla-manali': 'shimla-manali',
  'shimla-manali-volvo-couple': 'shimla-manali',
};

/**
 * Infer review key from free-text slug/title when no exact map exists.
 * Most specific patterns first so "shimla-manali" ≠ "manali".
 */
function inferKeyFromText(text) {
  const s = String(text || '').toLowerCase();
  if (!s) return null;
  if (s.includes('taxi') && (s.includes('shimla') || s.includes('manali') || s.includes('himachal'))) {
    return 'shimla-manali-taxi';
  }
  if (s.includes('spiti') || s.includes('kinnaur')) return 'spiti-kinnaur';
  if (
    (s.includes('shimla') && s.includes('manali')) ||
    s.includes('shimla-manali') ||
    s.includes('shimla_manali')
  ) {
    return 'shimla-manali';
  }
  if (s.includes('dharamshala') || s.includes('dalhousie') || s.includes('mcleod')) {
    return 'dharamshala';
  }
  if (
    s.includes('kashmir') ||
    s.includes('srinagar') ||
    s.includes('gulmarg') ||
    s.includes('pahalgam') ||
    s.includes('sonmarg')
  ) {
    return 'kashmir';
  }
  if (
    s.includes('ladakh') ||
    s.includes('leh') ||
    s.includes('pangong') ||
    s.includes('nubra')
  ) {
    return 'leh-ladakh';
  }
  if (s.includes('manali') || s.includes('solang') || s.includes('kasol')) return 'manali';
  if (s.includes('shimla') || s.includes('kufri') || s.includes('chail')) return 'shimla';
  if (s.includes('himachal')) return 'himachal';
  return null;
}

/** Resolve a page slug to a canonical review key (or null). */
export function resolveReviewKey(slugOrKey) {
  if (!slugOrKey) return null;
  if (VIDEO_REVIEWS[slugOrKey]) return slugOrKey;
  if (SLUG_TO_KEY[slugOrKey]) return SLUG_TO_KEY[slugOrKey];
  return inferKeyFromText(slugOrKey);
}

/** True when slug/key has a dedicated review set (not just the default mix). */
export function hasVideoReviews(slugOrKey) {
  return Boolean(resolveReviewKey(slugOrKey));
}

/**
 * Resolve video reviews for a page slug or explicit key.
 * Accepts either a VIDEO_REVIEWS key or a route slug.
 */
export function getVideoReviews(slugOrKey) {
  const key = resolveReviewKey(slugOrKey);
  if (key && VIDEO_REVIEWS[key]) return VIDEO_REVIEWS[key];
  return DEFAULT_VIDEO_REVIEWS;
}

/** Prefer package slug mapping, then destination, then default mix. */
export function getVideoReviewsForPackage(slug, destination) {
  if (hasVideoReviews(slug)) return getVideoReviews(slug);
  if (hasVideoReviews(destination)) return getVideoReviews(destination);
  return DEFAULT_VIDEO_REVIEWS;
}
